/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isNull from 'lodash.isnull';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {AccountLink, EntityStatus} from '@ion/core/src/types/entities';
import {dlsColor} from '../../theme/theme';
import Accordion, {IconPosition} from '../accordion/ExpansionPanel';
import GroupChooserRadioItem from './GroupChooserRadioItem';
import {scrollToGroup} from './GroupChooserUtils';
import SubAccountAccordion from './SubAccountAccordion';

/**
 * Defines the type for providing the prop passed through the global group chooser
 * component hierarchy which determines if the currently selected group is to be
 * automatically scrolled to. This feature has proven highly problematic and thus
 * can be disabled by calling applications by passing `disableAutoSelect` as `true`
 * to the `GlobalGroupChooser` component.
 */
export interface AutoSelectClient {
  disableAutoSelect?: boolean;
}

export interface GroupChooserAccordionProps extends AutoSelectClient {
  group: AccountLink;
  radioValue: string;
  crumbValue?: string;
  setRadioValue?: Function;
  openExpansionId?: string;
  expansionList?: string[];
  selectedSearchValue?: AccountLink;
  mainListLength?: number;
  isRootAccount?: boolean;
}

const useStyles = makeStyles(theme => ({
  accordionDetails : {
    padding : '0px 0px 0px 20px ',
    display : 'block'
  },
  radioItem : {
    '&&' : {
      borderBottom : `solid 1px ${dlsColor('grey5')}`,
      maxHeight    : '48px',
      minHeight    : '0px',
      padding      : '0 0 0 12px',
      '&:hover'    : {
        backgroundColor : dlsColor('paleBlue')
      },
      '& .MuiFormControlLabel-label' : {
        whiteSpace   : 'nowrap',
        maxWidth     : '240px',
        overflow     : 'hidden',
        textOverflow : 'ellipsis'
      }
    }
  },
  singleRadio : {
    paddingLeft : '48px !important'
  },
  matchedSelection : {
    backgroundColor : dlsColor('paleBlue')
  },
  crumbSelection : {
    backgroundColor : dlsColor('white')
  }
}));

/**
 * @GroupChooserAccordion
 *
 * Accordion for displaying a list of nested account options with any amount of
 * levels.
 *
 * ```typescript
 * import {GroupChooserAccordion} from '@ion/dls/src/components/groupchooser';
 *
 * <GroupChooserAccordion group={group}
 *   radioValue='Group1'
 *   setRadioValue={setRadioValue}
 *   openExpansionId='Group1'
 *   selectedSearchValue='Group2' />
 *
 * ```
 *
 * @param group group that is displayed in the accordion header
 * @param accountTree flattened account list with references to parents
 * @param radioValue value of the currently selected item from the list
 * @param setRadioValue callback to set the radio value
 * @param openExpansionId for controlling if the accordion panel needs to
 *   be expanded
 * @param selectedSearchValue user clicked on its chevron in the search results
 * @param isRootAccount if group being passed is at the root level so we don't auto scroll
 */
export const GroupChooserAccordion = ({
  group,
  radioValue,
  setRadioValue,
  openExpansionId,
  expansionList = [],
  selectedSearchValue,
  mainListLength,
  crumbValue = null,
  isRootAccount = false,
  disableAutoSelect = false
}: GroupChooserAccordionProps) => {
  const classes                                   = useStyles();
  const [expansionId, setExpansionId]             = useState(null);
  const currentAccordion                          = useRef(null);
  const searchedAccordion                         = useRef(null);
  const crumbAccordion                            = useRef(null);
  const radioItemRef                              = useRef(null);

  const {status, subAccounts, href} = group;
  const title                              = EntityLabel.get(group);
  const selectedMatch                      = href === selectedSearchValue?.href;
  const currentMatch                       = href === radioValue;
  const crumbMatch                         = href === crumbValue;

  useEffect(() => {
    if (openExpansionId) {
      setExpansionId(openExpansionId);
      if ((openExpansionId === radioValue) && !selectedSearchValue.href) {
        const currentRef = currentAccordion.current || radioItemRef.current;
        if (!isRootAccount && !disableAutoSelect) {
          scrollToGroup(currentRef, mainListLength);
        }
      }
    }
  }, [openExpansionId, mainListLength, disableAutoSelect]);

  useEffect(() => {
    if (selectedSearchValue && selectedMatch) {
      scrollToGroup(searchedAccordion?.current, mainListLength);
    }
  }, [selectedSearchValue, selectedMatch, mainListLength]);

  useEffect(() => {
    if (!isNull(crumbValue) && !selectedSearchValue.href && crumbAccordion?.current !== null) {
      const crumbEle = document.querySelectorAll('[class*=crumbSelection]');
      crumbEle[0]?.scrollIntoView({behavior: 'smooth'});
    }
  }, [crumbValue, selectedSearchValue]);

  const onToggleExpand = useCallback(function(panel, isExpanded) {
    setExpansionId(isExpanded ? panel : null);
  }, []);

  function getAccordionRef() {
    if (selectedMatch) {
      return searchedAccordion;
    }
    if (crumbMatch) {
      return crumbAccordion;
    }
    if (group.href === radioValue) {
      return currentAccordion;
    }
    return radioItemRef;
  }

  return (
    !!subAccounts?.length && status === EntityStatus.Enabled ? (
      <Accordion id={href}
        unmountOnExit={true}
        iconPosition={IconPosition.Start}
        ref={getAccordionRef()}
        header={
          <div onClick={function(event) { event.stopPropagation(); }}>
            <GroupChooserRadioItem label={title}
              value={group.href}
              ref={getAccordionRef()} />
          </div>
        }
        details={expansionId ? (
          <SubAccountAccordion subAccounts={subAccounts}
            radioValue={radioValue}
            crumbValue={crumbValue}
            setRadioValue={setRadioValue}
            expansionList={expansionList}
            selectedSearchValue={selectedSearchValue}
            mainListLength={mainListLength}
            disableAutoSelect={disableAutoSelect} />
        ) : undefined
        }
        onToggleExpand={onToggleExpand}
        expandedLayer={expansionId}
        summaryRootClass={classnames(
          classes.radioItem,
          (selectedMatch || currentMatch) ? classes.matchedSelection : crumbMatch ? classes.crumbSelection : null
        )}
        detailsRootOverride={classes.accordionDetails} />
    ) : (
      <div className={classnames(
        classes.radioItem,
        classes.singleRadio,
        currentMatch ? classes.matchedSelection : null
      )}>
        <GroupChooserRadioItem label={title}
          value={group.href}
          ref={getAccordionRef()} />
      </div>
    )
  );
};

export default GroupChooserAccordion;
